import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { format, differenceInCalendarDays, getISOWeek } from "date-fns";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vcr_admin_token";
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` });

const COLORS = { instagram: "#C1694F", google: "#6B7B52", amis: "#2D302A", autres: "#B8A88F" };
const CHANNEL_LABELS = { instagram: "Instagram", google: "Google", amis: "Ami(e)s", autres: "Autres" };
const euro = (v) => `${Number(v).toLocaleString("fr-FR")} €`;

const eachNight = (b) => {
  const nights = [];
  const start = new Date(`${b.start_date}T00:00:00`);
  const n = Math.max(differenceInCalendarDays(new Date(`${b.end_date}T00:00:00`), start), 1);
  for (let i = 0; i < n; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    nights.push(d);
  }
  return nights;
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-sand-bg border border-[var(--line)] p-5 flex-1 min-w-[160px]">
    <p className="text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">{label}</p>
    <p className="font-serif text-3xl font-light">{value}</p>
    {sub && <p className="text-xs text-ink-soft mt-1">{sub}</p>}
  </div>
);

export const StatsPanel = ({ onLogout, refreshKey }) => {
  const [bookings, setBookings] = useState([]);
  const [survey, setSurvey] = useState({ counts: {}, total: 0 });
  const [period, setPeriod] = useState("mois");
  const year = new Date().getFullYear();

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeaders() }),
        axios.get(`${API}/survey/stats`, { headers: authHeaders() }),
      ]);
      setBookings(b.data);
      setSurvey(s.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Impossible de charger les statistiques.");
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const today = format(new Date(), "yyyy-MM-dd");
  const staysDone = bookings.filter((b) => b.end_date < today).length;

  const caData = useMemo(() => {
    const withAmount = bookings.filter((b) => b.amount != null && b.amount > 0);
    if (period === "semaine") {
      const buckets = {};
      withAmount.forEach((b) => {
        const d = new Date(`${b.start_date}T00:00:00`);
        if (d.getFullYear() !== year) return;
        const k = `S${String(getISOWeek(d)).padStart(2, "0")}`;
        buckets[k] = (buckets[k] || 0) + b.amount;
      });
      return Object.keys(buckets).sort().map((k) => ({ name: k, ca: buckets[k] }));
    }
    if (period === "mois") {
      const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
      const data = months.map((m) => ({ name: m, ca: 0 }));
      withAmount.forEach((b) => {
        const d = new Date(`${b.start_date}T00:00:00`);
        if (d.getFullYear() === year) data[d.getMonth()].ca += b.amount;
      });
      return data;
    }
    const buckets = {};
    withAmount.forEach((b) => {
      const y = b.start_date.slice(0, 4);
      buckets[y] = (buckets[y] || 0) + b.amount;
    });
    return Object.keys(buckets).sort().map((k) => ({ name: k, ca: buckets[k] }));
  }, [bookings, period, year]);

  const caYear = bookings
    .filter((b) => b.amount != null && b.start_date.startsWith(String(year)))
    .reduce((s, b) => s + b.amount, 0);

  const occupancy = useMemo(() => {
    const perMonth = Array.from({ length: 12 }, (_, i) => ({
      name: ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"][i],
      nights: 0,
      days: new Date(year, i + 1, 0).getDate(),
    }));
    bookings.forEach((b) => {
      eachNight(b).forEach((d) => {
        if (d.getFullYear() === year) perMonth[d.getMonth()].nights += 1;
      });
    });
    return perMonth.map((m) => ({ name: m.name, taux: Math.min(Math.round((m.nights / m.days) * 100), 100) }));
  }, [bookings, year]);

  const occYear = Math.round(occupancy.reduce((s, m) => s + m.taux, 0) / 12);

  const pieData = Object.entries(survey.counts || {})
    .map(([k, v]) => ({ name: CHANNEL_LABELS[k], key: k, value: v }))
    .filter((d) => d.value > 0);

  return (
    <section className="mb-12 bg-sand-surface border border-[var(--line)] p-6" data-testid="stats-panel">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
        <h2 className="font-serif text-2xl font-light">Statistiques</h2>
      </div>

      <div className="flex flex-wrap gap-4 mb-8">
        <StatCard label="Séjours effectués" value={staysDone} sub="Réservations terminées" />
        <StatCard label={`CA ${year}`} value={euro(caYear)} sub="Montants renseignés sur les réservations" />
        <StatCard label={`Taux de remplissage ${year}`} value={`${occYear} %`} sub="Moyenne des 12 mois" />
        <StatCard label="Réponses au sondage" value={survey.total} sub="Canal d'acquisition" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-sand-bg border border-[var(--line)] p-5" data-testid="ca-chart">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs tracking-[0.2em] uppercase text-ink-soft">Chiffre d'affaires</p>
            <div className="flex gap-1">
              {["semaine", "mois", "année"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  data-testid={`ca-period-${p}`}
                  className={`text-[11px] px-3 py-1.5 border transition-colors capitalize ${
                    period === p ? "bg-ink text-sand-bg border-ink" : "border-[var(--line)] text-ink-soft hover:border-ink"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={caData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4DCCB" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6E6A5E" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6E6A5E" }} />
              <Tooltip formatter={(v) => euro(v)} contentStyle={{ background: "#F5EEE1", border: "1px solid #E4DCCB" }} />
              <Bar dataKey="ca" name="CA" fill="#C1694F" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-sand-bg border border-[var(--line)] p-5" data-testid="occupancy-chart">
          <p className="text-xs tracking-[0.2em] uppercase text-ink-soft mb-4">Taux de remplissage {year} (%)</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={occupancy}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4DCCB" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6E6A5E" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#6E6A5E" }} />
              <Tooltip formatter={(v) => `${v} %`} contentStyle={{ background: "#F5EEE1", border: "1px solid #E4DCCB" }} />
              <Line type="monotone" dataKey="taux" name="Taux" stroke="#6B7B52" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-sand-bg border border-[var(--line)] p-5 lg:col-span-2" data-testid="channel-chart">
          <p className="text-xs tracking-[0.2em] uppercase text-ink-soft mb-4">Canal d'acquisition (sondage visiteurs)</p>
          {pieData.length === 0 ? (
            <p className="text-sm text-ink-soft py-10 text-center">Aucune réponse au sondage pour l'instant.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name} (${e.value})`}>
                  {pieData.map((d) => <Cell key={d.key} fill={COLORS[d.key]} />)}
                </Pie>
                <Legend />
                <Tooltip contentStyle={{ background: "#F5EEE1", border: "1px solid #E4DCCB" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </section>
  );
};
